import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { jobHistoryQueryOptions, type JobHistoryEntry } from '@/entities/media-campaign';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';

/** Wording for the actions the audit trail records. */
const ACTION_LABELS: Record<string, string> = {
  get preflight_run() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.preflightRun');
  },
  get preflight_cleared() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.findingsCleared');
  },
  get node_edited() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.rowEdited');
  },
  get plan_replaced() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.planReImported');
  },
  get platform_bound() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.accountBound');
  },
  get job_published() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.published');
  },
  get finding_waived() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.findingWaived');
  },
  get advisory_dismissed() {
    return i18n.t('projects:deliveryJobReview.jobHistoryPanel.action.advisoryDismissed');
  },
};

/**
 * A one-line summary of what an entry actually did.
 *
 * Reads the counts a pre-flight run recorded rather than recomputing anything, so
 * the list still tells the truth about a run whose findings have since been
 * cleared or fixed.
 */
function describe(entry: JobHistoryEntry): string | null {
  const meta = entry.metadata;

  if (entry.action === 'preflight_run') {
    const parts: string[] = [
      i18n.t('projects:deliveryJobReview.jobHistoryPanel.toFix', {
        count: Number(meta.errors ?? 0),
      }),
      i18n.t('projects:deliveryJobReview.jobHistoryPanel.toCheck', {
        count: Number(meta.warnings ?? 0),
      }),
    ];
    const advice = Number(meta.advice ?? 0);
    if (advice > 0)
      parts.push(i18n.t('projects:deliveryJobReview.jobHistoryPanel.advisory', { count: advice }));
    return parts.join(i18n.t('projects:deliveryJobReview.jobHistoryPanel.listJoin'));
  }
  if (entry.action === 'node_edited') {
    const fields = Array.isArray(meta.fields) ? (meta.fields as string[]) : [];
    const name =
      typeof meta.name === 'string'
        ? meta.name
        : i18n.t('projects:deliveryJobReview.jobHistoryPanel.aRow');
    return fields.length > 0
      ? i18n.t('projects:deliveryJobReview.jobHistoryPanel.nameWithFields', {
          name,
          fields: fields.join(i18n.t('projects:deliveryJobReview.jobHistoryPanel.listJoin')),
        })
      : name;
  }
  if (entry.action === 'plan_replaced') {
    const platforms = Array.isArray(meta.platforms) ? (meta.platforms as string[]) : [];
    return platforms.length > 0
      ? platforms.join(i18n.t('projects:deliveryJobReview.jobHistoryPanel.listJoin'))
      : null;
  }
  if (entry.action === 'platform_bound') {
    return typeof meta.platform === 'string' ? meta.platform : null;
  }
  return null;
}

export interface JobHistoryPanelProps {
  open: boolean;
  jobId: string;
  onClose: () => void;
}

/**
 * Everything that has happened to this job, for audit.
 *
 * Server-side and append-only, so it survives a reload, a cleared pre-flight and a
 * re-import. Nothing here is recomputed from the current plan: the point is what
 * was true when each action was taken.
 */
export function JobHistoryPanel({ open, jobId, onClose }: JobHistoryPanelProps) {
  const { t } = useTranslation('projects');
  const { data, isLoading } = useQuery({
    ...jobHistoryQueryOptions(jobId),
    enabled: open && jobId !== '',
  });

  if (typeof document === 'undefined') return null;

  const entries = data ?? [];

  return createPortal(
    <aside
      className={`fixed bottom-0 right-0 top-0 z-50 flex w-96 flex-col border-l border-border bg-card shadow-xl transition-transform duration-slow ease-out ${
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      aria-hidden={!open}
      data-testid="job-history-panel"
    >
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {t('deliveryJobReview.jobHistoryPanel.history')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('deliveryJobReview.jobHistoryPanel.actionCount', { count: entries.length })}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('deliveryJobReview.jobHistoryPanel.closeHistory')}
          className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            {t('deliveryJobReview.jobHistoryPanel.nothingHasHappenedToThisJob')}
          </p>
        ) : (
          <ol className="divide-y divide-border">
            {entries.map((entry) => {
              const detail = describe(entry);
              return (
                <li key={entry.id} className="flex flex-col gap-0.5 px-4 py-3">
                  <span className="text-sm font-medium">
                    {ACTION_LABELS[entry.action] ?? entry.action}
                  </span>
                  {detail && <span className="text-xs text-foreground">{detail}</span>}
                  {entry.reason && (
                    <span className="text-xs italic text-muted-foreground">{entry.reason}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(entry.createdAt).toLocaleString()} · {entry.actorId}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </aside>,
    document.body,
  );
}
